/**
 * Pure signals for header turn / game-end chrome.
 * Roster and seat assignment stay on mateListVM; this dep only shapes chrome copy.
 */
import { computed } from '@preact/signals-react'
import type { RoomState } from '../../room/room.types'
import type { MateListVM, MateListVMPlayer } from '../mateListVM/mateListVM.types'
import { resolveGameEndOutcome } from './gameEndStatus'
import type { TurnIndicatorVM } from './turnIndicatorVM.types'

export type TurnIndicatorVMDeps = {
  mateListVM: MateListVM
  roomState: RoomState
}

function formatMateNames(names: string[]): string {
  const filtered = names.map((n) => n.trim()).filter(Boolean)
  if (filtered.length <= 1) return filtered[0] || ''
  if (filtered.length === 2) return `${filtered[0]} and ${filtered[1]}`
  return `${filtered.slice(0, -1).join(', ')} and ${filtered[filtered.length - 1]}`
}

/** Title-case seat label for chrome copy (e.g. blue → Blue; X stays X). */
function formatPlayerLabel(player: MateListVMPlayer | undefined, fallbackId: string): string {
  const raw = (player?.displayName || player?.id || fallbackId || '').trim()
  if (!raw) return fallbackId
  return raw.charAt(0).toUpperCase() + raw.slice(1)
}

export function makeTurnIndicatorVMDep(deps: TurnIndicatorVMDeps): TurnIndicatorVM {
  const { mateListVM, roomState } = deps

  const isGameEstablishedChallenge = computed(() => {
    return roomState.activeGame.value?.challengeConfiguration?.challengeSource === 'game'
  })

  const canSeveralPlayersAct = computed(() => {
    const activePlayers = roomState.activeGame.value?.ctx?.activePlayers
    return Boolean(activePlayers && Object.keys(activePlayers).length > 1)
  })

  const gameEndOutcome = computed(() => {
    const gameover = roomState.activeGame.value?.ctx?.gameover
    const ids = mateListVM.players.value.map((player) => player.id)
    return resolveGameEndOutcome(gameover, ids)
  })

  const isGameOver = computed(() => Boolean(gameEndOutcome.value))

  /**
   * Seat whose colors / piece the header turn chrome should paint.
   * During play: the active seat. On a win: the winner. On draw/over: none (neutral).
   */
  const turnIndicatorPlayerId = computed<string | undefined>(() => {
    const outcome = gameEndOutcome.value
    if (outcome?.kind === 'win') return outcome.playerId
    if (outcome) return undefined
    if (isGameEstablishedChallenge.value || canSeveralPlayersAct.value) return undefined
    return mateListVM.activePlayerId.value
  })

  /** Win / draw / over copy for the header when the game has ended. */
  function formatGameEndMessage(): string | null {
    const outcome = gameEndOutcome.value
    if (!outcome) return null
    if (outcome.kind === 'draw') return 'Draw'
    if (outcome.kind === 'over') return 'Game over'

    const id = outcome.playerId
    const winner = mateListVM.players.value.find((p) => p.id === id)
    const playerLabel = formatPlayerLabel(winner, id)

    const myId = mateListVM.ourMateId.value
    if (myId && winner?.mates.length === 1 && winner.mates[0]?.id === myId) {
      return `You win! (${playerLabel})`
    }

    const names = (winner?.mates ?? []).map((m) => m.name).filter(Boolean)
    const formatted = formatMateNames(names)
    if (formatted) return `${formatted} wins! (${playerLabel})`

    if (mateListVM.isSharedScreen.value && mateListVM.gameId.value) {
      const playerIndex = mateListVM.players.value.findIndex((p) => p.id === id)
      if (playerIndex >= 0) {
        return `Player ${playerIndex + 1} wins! (${playerLabel})`
      }
    }

    return `${playerLabel} wins!`
  }

  const turnMessageText = computed(() => {
    if (!mateListVM.hasActiveGame.value) return null

    const endMessage = formatGameEndMessage()
    if (endMessage) return endMessage

    // Game-established challenges do not have a turn owner; completion is shown
    // by the game UI. An opponent-established setup can instead let both seats act.
    if (isGameEstablishedChallenge.value) return null
    if (canSeveralPlayersAct.value) return 'Both players can act'

    const activePlayerId = mateListVM.activePlayerId.value
    if (!activePlayerId) return null

    const active = mateListVM.players.value.find((p) => p.id === activePlayerId)
    if (!active) return `Oh-oh: ${activePlayerId}'s Turn`

    const playerLabel = formatPlayerLabel(active, activePlayerId)

    const myId = mateListVM.ourMateId.value
    // Solo seat that is us: personalize; multi-mate seats always list names.
    if (myId && active.mates.length === 1 && active.mates[0]?.id === myId) {
      return `Your turn (${playerLabel})`
    }

    const names = active.mates.map((m) => m.name).filter(Boolean)
    const formatted = formatMateNames(names)
    if (formatted) return `${formatted}'s Turn (${playerLabel})`

    // Shared screen: if nobody is assigned to the active seat, fall back to a stable seat label.
    if (mateListVM.isSharedScreen.value && mateListVM.gameId.value) {
      const playerIndex = mateListVM.players.value.findIndex((p) => p.id === activePlayerId)
      if (playerIndex >= 0) {
        return `Player ${playerIndex + 1}'s Turn (${playerLabel})`
      }
    }

    return `${playerLabel}'s Turn`
  })

  const turnMessageDataAttrs = computed(() => {
    if (!mateListVM.hasActiveGame.value) return null

    const id = turnIndicatorPlayerId.value
    if (!id) return null

    const playerIndex = mateListVM.players.value.findIndex((p) => p.id === id)
    if (playerIndex < 0) return null

    return {
      'data-player-number': String(playerIndex + 1),
      'data-player-symbol': id,
    }
  })

  return {
    isGameOver,
    turnIndicatorPlayerId,
    turnMessageText,
    turnMessageDataAttrs,
  }
}
